// src/components/products/ProductsTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetProducts from "../../hooks/products/useGetProducts";
import useDeleteProduct from "../../hooks/products/useDeleteProduct";
import useDeleteAllProducts from "../../hooks/products/useDeleteAllProducts";
import useRestoreProduct from "../../hooks/products/useRestoreProduct";
import useRestoreAllProducts from "../../hooks/products/useRestoreAllProducts";
import useForceDeleteProduct from "../../hooks/products/useForceDeleteProduct";
import useForceDeleteAllProducts from "../../hooks/products/useForceDeleteAllProducts";

// Import hooks للحصول على Suppliers و Categories
import useGetSuppliersForSelect from "../../hooks/suppliers/useGetSuppliersForSelect";
import useGetCategoriesForSelect from "../../hooks/categories/useGetCategoriesForSelect";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import ProductsTableRow from "./Table/ProductsTableRow";
import ProductsTableColumns from "./Table/ProductsTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const ProductsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("products_list"),
            create: has("products_create"),
            edit: has("products_edit"),
            delete: has("products_delete"),
            forceDelete: has("products_force_delete"),
            archiveList: has("products_archive_list"),
            restore: has("products_restore"),
            restoreAll: has("products_restore-all-table"),
            deleteAll: has("products_delete-all-table"),
            massForceDeleteAll: has("products_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/products/archive");

    // CRUD Hooks
    const { deleteProduct, isDelete } = useDeleteProduct();
    const { deleteAllProducts, isDeleteAll } = useDeleteAllProducts();
    const { restoreProduct, isRestore } = useRestoreProduct();
    const { restoreAllProducts, isRestoreAll } = useRestoreAllProducts();
    const { forceDeleteProduct, isForceDelete } = useForceDeleteProduct();
    const { forceDeleteAllProducts, isForceDeleteAll } = useForceDeleteAllProducts();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/products",
        deleteSingle: deleteProduct,
        deleteAll: deleteAllProducts,
        restoreSingle: restoreProduct,
        restoreAll: restoreAllProducts,
        forceDeleteSingle: forceDeleteProduct,
        forceDeleteAll: forceDeleteAllProducts,
        isArchive,
    });

    // Local State للفلاتر
    const [supplierFilter, setSupplierFilter] = React.useState("");
    const [categoryFilter, setCategoryFilter] = React.useState("");

    // جلب Suppliers و Categories للـ Select
    const { suppliers } = useGetSuppliersForSelect();
    const { options: categories } = useGetCategoriesForSelect();

    // ✅ تعديل sortBy لإضافة اللاحقة (_ar أو _en) حسب اللغة
    const adjustedSortBy = useMemo(() => {
        if (!sortBy) return undefined;
        
        // الحقول التي لها عمودين منفصلين (title_ar, title_en) نرسلها كما هي
        if (sortBy === 'title_ar' || sortBy === 'title_en') {
            return sortBy;
        }
        
        // الحقول التي لها عمود واحد نضيف لها اللاحقة حسب اللغة
        const fieldsNeedingLangSuffix = ['product_type', 'category', 'transportation'];
        
        if (fieldsNeedingLangSuffix.includes(sortBy)) {
            return `${sortBy}_${isArabic ? 'ar' : 'en'}`;
        }
        
        return sortBy;
    }, [sortBy, isArabic]);

    const filters = useMemo(
        () => ({
            search: search || undefined,
            supplier_id: supplierFilter || undefined,
            category_id: categoryFilter || undefined,
            sort_by: adjustedSortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, supplierFilter, categoryFilter, adjustedSortBy, sortDir, isArchive]
    );

    // ✅ تحويل Suppliers و Categories إلى Options للـ Select
    const supplierOptions = useMemo(() => {
        const allOption = {
            value: "",
            label: { ar: "كل الموردين", en: "All Suppliers" }
        };

        const suppliersList = (suppliers || []).map((supplier) => ({
            value: supplier.value,
            label: {
                ar: supplier.label,
                en: supplier.label
            },
        }));

        return [allOption, ...suppliersList];
    }, [suppliers]);

    const categoryOptions = useMemo(() => {
        const allOption = {
            value: "",
            label: { ar: "كل الفئات", en: "All Categories" }
        };

        const categoriesList = (categories || []).map((category) => ({
            value: category.value,
            label: {
                ar: category.label,
                en: category.label
            },
        }));

        return [allOption, ...categoriesList];
    }, [categories]);

    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "supplier",
                label: { ar: "المورد", en: "Supplier" },
                options: supplierOptions,
                value: supplierFilter,
                onChange: setSupplierFilter,
            },
            {
                type: "select",
                name: "category",
                label: { ar: "الفئة", en: "Category" },
                options: categoryOptions,
                value: categoryFilter,
                onChange: setCategoryFilter,
            },
        ],
        [supplierFilter, categoryFilter, supplierOptions, categoryOptions]
    );

    const handleClearAllFilters = () => {
        setSupplierFilter("");
        setCategoryFilter("");
    };

    const { isLoading, error, products, meta } = useGetProducts(page, perPage, filters);

    const items = useMemo(() => products?.items || [], [products]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <ProductsTableRow
            key={params.item.id}
            product={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "المنتجات" : "Products";
    const subtitle = isArchive
        ? isArabic
            ? "عرض المنتجات المحذوفة (الأرشيف)"
            : "Archived products (soft deleted)"
        : isArabic
            ? "إدارة المنتجات المتاحة في النظام"
            : "Manage available products in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة منتج"
            : "Create new product";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllProducts({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission"
                );
                return;
            }

            navigate("/products/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/products/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/products"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={ProductsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا توجد منتجات في الأرشيف" : "لا توجد منتجات"}
                emptyLabelEn={isArchive ? "No archived products" : "No products"}
                loadErrorAr="حدث خطأ أثناء تحميل المنتجات"
                loadErrorEn="Failed to load products"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default ProductsTable;
